import React, { useState, useMemo, startTransition } from 'react'
import Container from '@/components/Container'
import Section from '@/components/Section'
import SectionHeader from '@/components/SectionHeader'
import SectionSubHeading from '@/components/SectionSubHeading'
import { isNonEmptyArray } from '../../utils/checks'
import { searchForFacetValues } from '../../utils/search/searchForFacetValues'
import NearbySkeletonCard from '../organisms/Loaders/NearbySkeletonCard.jsx'
import {
  POPULAR_TREATMENT_TERMS,
  POPULAR_TREATMENT_TYPES_DISPLAY_THRESHOLD,
  popularTreatmentOptions
} from '../../domain/useCases/popularTreatment/constants'
import { sortPopularTreatmentTypes } from '@/domain/useCases/popularTreatment/sortPopularTreatmentTypes.ts'
import NewsItem from '../molecules/NewsItem/NewsItem'
import transformTextToSlug from '../../utils/transformTextToSlug.js'
import HorizontalScroll from '../atoms/HorizontalScroll/HorizontalScroll'
import { localizeCenterToCentre } from '../../utils/localizeCenter'

const PopularTreatmentCarousel = ({
  title,
  locationSlug,
  parentLocation,
  shouldDefaultRegionalize = false,
  shouldDisplay
}) => {
  const [searching, setSearching] = useState(true)
  const [popularTreatments, setPopularTreatments] = useState([])
  useMemo(() => {
    searchForFacetValues({
      location: title,
      locationSlug,
      parentLocation,
      terms: POPULAR_TREATMENT_TERMS
    })
      .then(
        (res) => {
          return res
        },
        (err) => {
          console.error('error setting popular treatments', err)
          return null
        }
      )
      .then((treatments) => {
        const cards = Object.entries(treatments).reduce(
          (acc, [label, count]) => {
            const treatmentType = POPULAR_TREATMENT_TERMS.find(
              (term) => term.label === label
            )

            if (
              treatmentType &&
              count >= POPULAR_TREATMENT_TYPES_DISPLAY_THRESHOLD
            ) {
              const card = {
                ...treatmentType,
                count
              }
              acc.push(card)
              return acc
            }

            return acc
          },
          []
        )
        if (!isNonEmptyArray(cards)) {
          return null
        }
        return cards.sort(sortPopularTreatmentTypes).map((card) => (
          <NewsItem
            fluid
            key={card.label}
            subtitle={`${card.count} ${
              shouldDefaultRegionalize ? `Centres` : `Centers`
            } in ${title}`}
            description={popularTreatmentOptions[card.label]?.description || ''}
            title={
              shouldDefaultRegionalize
                ? localizeCenterToCentre(
                    popularTreatmentOptions[card.label]?.label || ''
                  )
                : popularTreatmentOptions[card.label]?.label
            }
            noReadMore
            link={card.slug.substring(1)}
            rawImage
            blogImage={`
    https://res.cloudinary.com/rehabpath/image/upload/e_gen_restore/c_fill,w_640,h_400,q_15/f_auto/taxonomy%20images/${
      card.blogImageSlug || transformTextToSlug(card.label)
    }
    `}
            isPopularTreatmentTypesCarousel={true}
            shouldOpenNewTabOnClick={false}
          />
        ))
      })
      .then((cards) => {
        startTransition(() => {
          setPopularTreatments(cards)
        })
      })
      .finally(() => {
        setSearching(false)
      })
  }, [])

  if (!shouldDisplay || (!searching && !isNonEmptyArray(popularTreatments))) {
    return null
  }

  return (
    <Section>
      <SectionHeader>
        <h3 className="font-FieldsVariable text-lg font-medium leading-6 text-gray-1100">
          Popular Types of Rehab in {title}
        </h3>
        <SectionSubHeading tag="p">
          Explore rehab options by level of care, tailored to specific needs, or
          based on treatment approach.
        </SectionSubHeading>
      </SectionHeader>
      {searching && (
        <HorizontalScroll
          alwaysScroll
          count={4}
          minWidth={'400px'}
          maxWidth="20vw"
          id="treatment"
        >
          {POPULAR_TREATMENT_TERMS.map((item, index) => (
            <NearbySkeletonCard key={index} />
          ))}
        </HorizontalScroll>
      )}
      {!searching && isNonEmptyArray(popularTreatments) && (
        <HorizontalScroll
          alwaysScroll
          count={4}
          minWidth={'400px'}
          maxWidth="20vw"
          id="treatment"
        >
          {popularTreatments}
        </HorizontalScroll>
      )}{' '}
    </Section>
  )
}

export default PopularTreatmentCarousel
